import { error as logError } from './logger';
import { apiUrl } from './apiUrl';

/**
 * Bootstrap Badge variant per genre for visual distinction.
 */
export const getGenreBadgeVariant = (genre) => {
  if (!genre || typeof genre !== 'string') return 'secondary';
  const g = genre.trim().toLowerCase();
  const map = {
    comedia: 'success',
    comedy: 'success',
    drama: 'primary',
    'acción': 'danger',
    action: 'danger',
    aventura: 'warning',
    adventure: 'warning',
    terror: 'dark',
    horror: 'dark',
    thriller: 'danger',
    suspense: 'danger',
    'ciencia ficción': 'info',
    'ciencia ficcion': 'info',
    'sci-fi': 'info',
    cienciaficcion: 'info',
    romance: 'danger',
    'animación': 'info',
    animacion: 'info',
    animation: 'info',
    documental: 'secondary',
    documentary: 'secondary',
    musical: 'warning',
    'fantasía': 'info',
    fantasia: 'info',
    fantasy: 'info',
    crimen: 'dark',
    crime: 'dark',
    misterio: 'secondary',
    mystery: 'secondary',
    western: 'warning',
    belica: 'dark',
    'bélica': 'dark',
    guerra: 'dark',
    war: 'dark',
    familia: 'success',
    family: 'success',
    historia: 'secondary',
    history: 'secondary',
    deporte: 'warning',
    sport: 'warning',
    musica: 'info',
    'música': 'info',
    music: 'info',
    rock: 'danger',
    pop: 'warning',
    electronica: 'info',
    'electrónica': 'info',
    jazz: 'info',
    indie: 'secondary',
    alternativa: 'secondary',
    'hip-hop': 'primary',
    hiphop: 'primary',
    latina: 'warning',
    clasica: 'secondary',
    'clásica': 'secondary'
  };
  return map[g] || 'secondary';
};

/**
 * Normalize genre field (string or array) into an array of labels.
 */
export const parseGenres = (genre) => {
  if (!genre) return [];
  if (Array.isArray(genre)) return genre.filter(Boolean).map((g) => String(g).trim());
  if (typeof genre === 'string') return genre.split(/[,/]+/).map((g) => g.trim()).filter(Boolean);
  return [];
};

/**
 * Format a date as relative time ("hace 3 minutos", "3 minutes ago", etc.).
 */
export const formatRelativeDate = (value, language = 'es') => {
  if (value == null || value === '') return null;
  const d = typeof value === 'object' && value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diffMs = now - d;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffSec / 3600);
  const diffDays = Math.floor(diffSec / 86400);
  const threeMonthsDays = 90;
  const isEn = language === 'en';

  if (diffSec < 0) return isEn ? 'now' : 'ahora';
  if (diffSec < 60) return isEn ? 'just now' : 'hace un momento';
  if (diffMin < 60) {
    if (isEn) return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
    return diffMin === 1 ? 'hace 1 minuto' : `hace ${diffMin} minutos`;
  }
  if (diffHours < 24) {
    if (isEn) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    return diffHours === 1 ? 'hace 1 hora' : `hace ${diffHours} horas`;
  }
  if (diffDays < threeMonthsDays) {
    if (isEn) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
    return diffDays === 1 ? 'hace 1 día' : `hace ${diffDays} días`;
  }
  return isEn ? 'several months ago' : 'hace varios meses';
};

/**
 * Load data from PostgreSQL (per user/session). "filename" is a resource id, not a disk file.
 */
export const loadData = async (filename) => {
  try {
    const response = await fetch(apiUrl(`/api/load-data/${filename}`), { credentials: 'include', cache: 'no-store' });
    if (response.ok) {
      return await response.json();
    }
    return null;
  } catch (error) {
    logError(`Error loading data (${filename}):`, error);
    return null;
  }
};

/** @deprecated Use loadData */
export const loadJSONData = loadData;

/**
 * Save data to PostgreSQL (per user/session).
 */
export const saveData = async (filename, data) => {
  try {
    const response = await fetch(apiUrl(`/api/save-data/${filename}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    return true;
  } catch (error) {
    logError(`Error saving data (${filename}):`, error);
    return false;
  }
};

/** @deprecated Use saveData */
export const saveJSONData = saveData;

/**
 * Insert or update a single watched/listened item (avoids sending the full history).
 */
export const upsertWatchedItem = async (contentType, item) => {
  try {
    const response = await fetch(apiUrl('/api/watched-item'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType, item }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    return true;
  } catch (error) {
    logError(`Error upserting watched item (${contentType}):`, error);
    throw error;
  }
};

/**
 * Delete a single watched/listened item by id.
 */
export const deleteWatchedItem = async (contentType, contentId) => {
  try {
    const response = await fetch(
      apiUrl(`/api/watched-item/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`),
      { method: 'DELETE', credentials: 'include' }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    return true;
  } catch (error) {
    logError(`Error deleting watched item (${contentType}, ${contentId}):`, error);
    throw error;
  }
};

/**
 * Insert or update a single recommendation.
 */
export const upsertRecommendationItem = async (contentType, item) => {
  try {
    const response = await fetch(apiUrl('/api/recommendation-item'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType, item }),
      credentials: 'include'
    });

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    return true;
  } catch (error) {
    logError(`Error upserting recommendation (${contentType}):`, error);
    throw error;
  }
};

/**
 * Delete a single recommendation by content id.
 */
export const deleteRecommendationItem = async (contentType, contentId) => {
  try {
    const response = await fetch(
      apiUrl(`/api/recommendation-item/${encodeURIComponent(contentType)}/${encodeURIComponent(contentId)}`),
      { method: 'DELETE', credentials: 'include' }
    );

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(`HTTP ${response.status}: ${errorText}`);
      error.status = response.status;
      throw error;
    }

    const data = await response.json().catch(() => ({}));
    return data.removed !== false;
  } catch (error) {
    logError(`Error deleting recommendation (${contentType}, ${contentId}):`, error);
    throw error;
  }
};

/**
 * Combina recomendaciones de diferentes tipos
 * @param {Array} movieRecs - Recomendaciones de películas
 * @param {Array} seriesRecs - Recomendaciones de series
 * @param {Array} musicRecs - Recomendaciones de música
 * @returns {Array} Recomendaciones combinadas
 */
export const combineRecommendations = (movieRecs = [], seriesRecs = [], musicRecs = []) => {
  return [
    ...movieRecs.map(rec => ({ ...rec, category: 'movies' })),
    ...seriesRecs.map(rec => ({ ...rec, category: 'series' })),
    ...musicRecs.map(rec => ({ ...rec, category: 'music' }))
  ];
};

/**
 * Filtra recomendaciones por tipo
 * @param {Array} recommendations - Recomendaciones a filtrar
 * @param {string} type - Tipo de contenido (movie, series, music)
 * @returns {Array} Recomendaciones filtradas
 */
export const filterByType = (recommendations, type) => {
  return recommendations.filter(rec => rec.type === type);
};

/**
 * Busca contenido en recomendaciones
 * @param {Array} recommendations - Recomendaciones donde buscar
 * @param {string} query - Término de búsqueda
 * @returns {Array} Resultados de búsqueda
 */
export const searchRecommendations = (recommendations, query) => {
  if (!query || query.trim() === '') return recommendations;
  
  const searchTerm = query.toLowerCase();
  return recommendations.filter(rec => 
    rec.title.toLowerCase().includes(searchTerm) ||
    (rec.artist && rec.artist.toLowerCase().includes(searchTerm)) ||
    (rec.actors && rec.actors.some(actor => actor.toLowerCase().includes(searchTerm))) ||
    (rec.director && rec.director.toLowerCase().includes(searchTerm)) ||
    rec.genre.toLowerCase().includes(searchTerm)
  );
};

/**
 * Genera un ID único para nuevo contenido
 * @param {string} type - Tipo de contenido
 * @returns {string} ID único
 */
export const generateUniqueId = (type) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `${type}_${timestamp}_${random}`;
};

/**
 * Valida los datos de una recomendación
 * @param {Object} recommendation - Recomendación a validar
 * @returns {Object} Resultado de validación
 */
export const validateRecommendation = (recommendation) => {
  const errors = [];
  
  if (!recommendation.title || recommendation.title.trim() === '') {
    errors.push('El título es requerido');
  }
  
  if (!recommendation.type || !['movie', 'series', 'music'].includes(recommendation.type)) {
    errors.push('El tipo debe ser movie, series o music');
  }
  
  if (!recommendation.genre || recommendation.genre.trim() === '') {
    errors.push('El género es requerido');
  }
  
  if (recommendation.year && (isNaN(recommendation.year) || recommendation.year < 1900 || recommendation.year > new Date().getFullYear() + 5)) {
    errors.push('El año debe ser válido');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Actualiza el historial de contenido visto
 * @param {Array} watchedContent - Contenido ya visto
 * @param {Object} newContent - Nuevo contenido a agregar
 * @returns {Array} Historial actualizado
 */
export const updateWatchedContent = (watchedContent, newContent) => {
  const existingIndex = watchedContent.findIndex(item => item.id === newContent.id);
  
  if (existingIndex >= 0) {
    // Actualizar contenido existente
    watchedContent[existingIndex] = {
      ...watchedContent[existingIndex],
      ...newContent,
      date_watched: new Date().toISOString().split('T')[0]
    };
  } else {
    // Agregar nuevo contenido
    watchedContent.push({
      ...newContent,
      date_watched: new Date().toISOString().split('T')[0]
    });
  }
  
  return watchedContent;
};

/**
 * Remueve contenido visto de las recomendaciones
 * @param {Array} recommendations - Recomendaciones actuales
 * @param {Array} watchedContent - Contenido ya visto
 * @returns {Array} Recomendaciones filtradas
 */
export const removeWatchedFromRecommendations = (recommendations, watchedContent) => {
  const watchedIds = watchedContent.map(item => item.id);
  return recommendations.filter(rec => !watchedIds.includes(rec.id));
};

/**
 * Descarga un archivo JSON específico
 * @param {string} filename - Nombre del archivo
 * @param {Object} data - Datos a descargar
 */
export const downloadJSONFile = (filename, data) => {
  try {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log(`📥 Archivo ${filename} descargado exitosamente`);
  } catch (error) {
    console.error(`❌ Error descargando ${filename}:`, error);
  }
};

/**
 * Muestra en consola los datos del recurso (desde BD vía API).
 */
export const showFileContent = async (filename) => {
  const data = await loadData(filename);
  if (data != null) {
    const content = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
    console.log(`📁 Datos de ${filename} (BD):`);
    console.log('='.repeat(50));
    console.log(content);
    console.log('='.repeat(50));
  } else {
    console.log(`No hay datos para ${filename}`);
  }
};

/**
 * Calcula estadísticas del usuario
 * @param {Object} userData - Datos del usuario
 * @returns {Object} Estadísticas calculadas
 */
export const calculateUserStats = (userData) => {
  const { movies = [], series = [], music = [] } = userData;
  const allWatched = [...movies, ...series, ...music];
  
  const stats = {
    totalWatched: allWatched.length,
    moviesWatched: movies.length,
    seriesWatched: series.length,
    musicListened: music.length,
    averageRating: 0,
    favoriteGenres: [],
    favoriteActors: [],
    totalHours: 0
  };
  
  if (allWatched.length > 0) {
    // Calcular calificación promedio
    const totalRating = allWatched.reduce((sum, item) => {
      return sum + (item.ratings?.user_rating || 0);
    }, 0);
    stats.averageRating = totalRating / allWatched.length;
    
    // Calcular géneros favoritos
    const genreCount = {};
    allWatched.forEach(item => {
      if (item.genre) {
        genreCount[item.genre] = (genreCount[item.genre] || 0) + 1;
      }
    });
    stats.favoriteGenres = Object.entries(genreCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([genre]) => genre);
    
    // Calcular actores favoritos
    const actorCount = {};
    allWatched.forEach(item => {
      if (item.actors) {
        item.actors.forEach(actor => {
          actorCount[actor] = (actorCount[actor] || 0) + 1;
        });
      }
    });
    stats.favoriteActors = Object.entries(actorCount)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([actor]) => actor);
  }
  
  return stats;
};
